import { useLazyQuery } from '@apollo/client';
import { BanknotesIcon } from '@heroicons/react/24/outline';
import { Skeleton, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  addDays,
  addHours,
  endOfDay,
  startOfDay,
  startOfMonth,
} from 'date-fns';
import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import EmptyState from '../../components/EmptyState/EmptyState';
import { ErrorAlert } from '../../components/ErrorAlert/ErrorAlert';
import Page from '../../components/Page/Page';
import Panel from '../../components/Panel/Panel';
import StatCard from '../../components/StatCard/StatCard';
import { PaidConversionResult } from '../../graphql/graphql';
import { PAID_CONVERSION } from '../../graphql/queries-gql';

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 16px;
  align-items: start;
`;

const StatRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 12px;
  font-size: 13px;
`;

const StatRowLabel = styled.span`
  color: ${(props) => props.theme.secondaryTextColor};
`;

const StatRowValue = styled.span<{ $tone?: 'green' | 'red' }>`
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  color: ${(props) =>
    props.$tone ? props.theme.colors[props.$tone] : props.theme.textColor};
`;

const BreakdownRow = styled.div<{ $header?: boolean }>`
  display: grid;
  grid-template-columns: minmax(180px, 2fr) 1fr 1fr 1fr;
  align-items: center;
  gap: 16px;
  padding: ${(props) => (props.$header ? '10px 18px' : '12px 18px')};
  border-bottom: 1px solid ${(props) => props.theme.border};
  transition: background-color 0.15s ease;

  &:last-child {
    border-bottom: 0;
  }

  ${(props) =>
    props.$header
      ? ''
      : `&:hover { background-color: ${props.theme.surfaceHover}; }`}
`;

const HeaderCell = styled.span<{ $left?: boolean }>`
  font-size: 11.5px;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  text-align: ${(props) => (props.$left ? 'left' : 'right')};
  color: ${(props) => props.theme.mutedTextColor};
`;

const SourceCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
`;

const SourceName = styled.span`
  font-size: 13.5px;
  font-weight: 500;
  color: ${(props) => props.theme.textColor};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

// Share of the largest source, so the ranking reads at a glance
const ShareTrack = styled.div`
  height: 4px;
  border-radius: 2px;
  background: ${(props) => props.theme.surfaceActive};
  overflow: hidden;
`;

const ShareFill = styled.div<{ $percent: number }>`
  width: ${(props) => props.$percent}%;
  height: 100%;
  border-radius: 2px;
  background: ${(props) => props.theme.colors.primary};
`;

const NumberCell = styled.span<{ $strong?: boolean }>`
  font-size: 13.5px;
  font-weight: ${(props) => (props.$strong ? 600 : 400)};
  text-align: right;
  font-variant-numeric: tabular-nums;
  color: ${(props) =>
    props.$strong ? props.theme.textColor : props.theme.secondaryTextColor};
`;

enum DateRange {
  Today,
  Yesterday,
  ThisMonth,
  Last24Hours,
  Last3Days,
  Last7Days,
  Last30Days,
  SpecificDate,
}

const formatCR = (numerator: number, denominator: number): string => {
  if (denominator === 0) return '0.0%';
  return ((numerator / denominator) * 100).toFixed(1) + '%';
};

const PaidConversion = () => {
  const [fromDate, setFromDate] = useState<Date>(startOfDay(new Date()));
  const [toDate, setToDate] = useState<Date>(new Date());
  const [platform, setPlatform] = useState<string | undefined>('ios');
  const [dateRange, setDateRange] = useState<DateRange>(DateRange.Today);
  const [fetchPaidConversion, { data, loading, error }] = useLazyQuery<{
    paidConversion: PaidConversionResult;
  }>(PAID_CONVERSION, {
    fetchPolicy: 'network-only',
    variables: {
      args: {
        fromDate,
        toDate,
        platform,
      },
    },
  });

  useEffect(() => {
    if (!data && !loading && !error) {
      fetchPaidConversion();
    }
  }, [data, loading, error, fetchPaidConversion]);

  const handleDateRangeChange = (range: DateRange) => {
    setDateRange(range);
    switch (range) {
      case DateRange.Today:
        setFromDate(startOfDay(new Date()));
        setToDate(new Date());
        break;
      case DateRange.Yesterday:
        setFromDate(startOfDay(addDays(new Date(), -1)));
        setToDate(endOfDay(addDays(new Date(), -1)));
        break;
      case DateRange.ThisMonth:
        setFromDate(startOfMonth(new Date()));
        setToDate(new Date());
        break;
      case DateRange.Last24Hours:
        setFromDate(addHours(new Date(), -24));
        setToDate(new Date());
        break;
      case DateRange.Last3Days:
        setFromDate(addDays(new Date(), -3));
        setToDate(new Date());
        break;
      case DateRange.Last7Days:
        setFromDate(addDays(new Date(), -7));
        setToDate(new Date());
        break;
      case DateRange.Last30Days:
        setFromDate(addDays(new Date(), -30));
        setToDate(new Date());
        break;
      case DateRange.SpecificDate:
        break;
    }
  };

  const handleSpecificDateChange = (date: Date | null) => {
    if (date) {
      setDateRange(DateRange.SpecificDate);
      setFromDate(startOfDay(date));
      setToDate(endOfDay(date));
    }
  };

  const stats = data?.paidConversion;

  const sortedSources = useMemo(() => {
    if (!stats?.sources) return [];
    return [...stats.sources].sort((a, b) => b.count - a.count);
  }, [stats?.sources]);

  const largestSourceCount = sortedSources[0]?.count ?? 0;

  return (
    <Page
      title="Paid conversion"
      description="New subscriptions measured against users created in the selected period."
      maxWidth={1280}
      actions={
        <ToggleButtonGroup
          value={platform}
          exclusive
          onChange={(_, value) => setPlatform(value)}
          size="small"
        >
          <ToggleButton value="ios">iOS</ToggleButton>
          <ToggleButton value="android">Android</ToggleButton>
        </ToggleButtonGroup>
      }
      toolbar={
        <>
          <ToggleButtonGroup
            value={dateRange}
            exclusive
            onChange={(_, value) =>
              value !== null && handleDateRangeChange(value)
            }
            size="small"
          >
            <ToggleButton value={DateRange.Today}>Today</ToggleButton>
            <ToggleButton value={DateRange.Yesterday}>Yesterday</ToggleButton>
            <ToggleButton value={DateRange.ThisMonth}>This month</ToggleButton>
            <ToggleButton value={DateRange.Last24Hours}>24 hours</ToggleButton>
            <ToggleButton value={DateRange.Last3Days}>3 days</ToggleButton>
            <ToggleButton value={DateRange.Last7Days}>7 days</ToggleButton>
            <ToggleButton value={DateRange.Last30Days}>30 days</ToggleButton>
          </ToggleButtonGroup>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="Pick a date"
              value={dateRange === DateRange.SpecificDate ? fromDate : null}
              onChange={handleSpecificDateChange}
              disableFuture
              slotProps={{
                textField: {
                  size: 'small',
                  sx: { width: 180 },
                },
              }}
            />
          </LocalizationProvider>
        </>
      }
    >
      {error && <ErrorAlert error={error} />}

      {loading ? (
        <>
          <SummaryGrid>
            {[...Array(3)].map((_, i) => (
              <Skeleton key={i} variant="rounded" height={132} />
            ))}
          </SummaryGrid>
          <Skeleton variant="rounded" height={260} />
        </>
      ) : (
        stats && (
          <>
            <SummaryGrid>
              <StatCard
                label="Total subscriptions"
                value={stats.totalSubscriptions}
              >
                <StatRow>
                  <StatRowLabel>Total CR</StatRowLabel>
                  <StatRowValue>
                    {formatCR(stats.totalSubscriptions, stats.totalUsersCreated)}
                  </StatRowValue>
                </StatRow>
                <StatRow>
                  <StatRowLabel>Onboarding finished CR</StatRowLabel>
                  <StatRowValue>
                    {formatCR(
                      stats.totalSubscriptions,
                      stats.totalOnboardingFinished,
                    )}
                  </StatRowValue>
                </StatRow>
                {stats.purchasesFromExistingUsers > 0 && (
                  <StatRow>
                    <StatRowLabel>From existing users</StatRowLabel>
                    <StatRowValue $tone="red">
                      {stats.purchasesFromExistingUsers}
                    </StatRowValue>
                  </StatRow>
                )}
              </StatCard>

              <StatCard label="Users created" value={stats.totalUsersCreated} />

              <StatCard
                label="Onboarding finished"
                value={stats.totalOnboardingFinished}
              >
                <StatRow>
                  <StatRowLabel>Completion rate</StatRowLabel>
                  <StatRowValue $tone="green">
                    {formatCR(
                      stats.totalOnboardingFinished,
                      stats.totalUsersCreated,
                    )}
                  </StatRowValue>
                </StatRow>
              </StatCard>
            </SummaryGrid>

            {sortedSources.length === 0 ? (
              <EmptyState
                Icon={BanknotesIcon}
                title="No subscription data for this period"
                description="Try a longer date range or the other platform."
              />
            ) : (
              <Panel title="Subscriptions by source" padding={0}>
                <BreakdownRow $header>
                  <HeaderCell $left>Source</HeaderCell>
                  <HeaderCell>Count</HeaderCell>
                  <HeaderCell>Total CR</HeaderCell>
                  <HeaderCell>Onboarding CR</HeaderCell>
                </BreakdownRow>

                {sortedSources.map((source) => (
                  <BreakdownRow key={source.source}>
                    <SourceCell>
                      <SourceName>{source.source}</SourceName>
                      <ShareTrack>
                        <ShareFill
                          $percent={
                            largestSourceCount > 0
                              ? (source.count / largestSourceCount) * 100
                              : 0
                          }
                        />
                      </ShareTrack>
                    </SourceCell>
                    <NumberCell $strong>{source.count}</NumberCell>
                    <NumberCell>
                      {formatCR(source.count, stats.totalUsersCreated)}
                    </NumberCell>
                    <NumberCell>
                      {formatCR(source.count, stats.totalOnboardingFinished)}
                    </NumberCell>
                  </BreakdownRow>
                ))}
              </Panel>
            )}
          </>
        )
      )}
    </Page>
  );
};

export default PaidConversion;
